/**
 * Per-module Analytics configurations.
 * Each export passes through to <DataFilesAnalytics endpoint kpis charts />.
 */
import { Package, Boxes, Layers, Globe, MapPin, FileText, Scroll, Group as GroupIcon, Hash, DollarSign, CheckCircle2, BarChart2, FileSpreadsheet, Receipt } from 'lucide-react';
import { fmtMoney } from './DataFilesAnalytics';

export const itemsConfig = {
  endpoint: '/api/data/items-dashboard',
  testIdPrefix: 'items-analytics',
  kpis: [
    { key: 'total', label: 'Total Items', icon: Package, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, subKey: 'inactive', subLabel: 'inactive', accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'favorites', label: 'Favorites', icon: BarChart2, accent: 'bg-amber-50 text-amber-600' },
    { key: 'inactive', label: 'Inactive', icon: Boxes, accent: 'bg-zinc-100 text-zinc-600' },
  ],
  charts: [
    [
      { kind: 'pie', dataKey: 'by_type', title: 'Items by Type', subtitle: 'Inventory, service, asset, etc. — click to drill', drillTo: { resource: '/api/data/items', queryKey: 'item_type', columns: [{key:'item_code',label:'Code'},{key:'description',label:'Description'},{key:'unit_cost',label:'Unit Cost',align:'right',format:(v)=>v?`$${Number(v).toLocaleString()}`:'—'}] } },
      { kind: 'bar-h', dataKey: 'by_category', title: 'Top 10 Categories', subtitle: 'By item count — click a category', colorOffset: 1, drillTo: { resource: '/api/data/items', queryKey: 'category_code', columns: [{key:'item_code',label:'Code'},{key:'description',label:'Description'},{key:'category_code',label:'Category'}] } },
    ],
    [
      { kind: 'bar', dataKey: 'cost_bands', title: 'Items by Unit Cost', subtitle: 'Price band distribution', colorOffset: 2 },
      { kind: 'table', dataKey: 'top_items', title: 'Top 10 Items by Spend', subtitle: 'Click an item to see contributing orders', span: 'md:col-span-2', valueKey: 'value', extraKey: 'qty', extraLabel: 'units', drillTo: { resource: '/api/orders', queryKey: 'item_code', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?`$${Number(v).toLocaleString()}`:'—'}] } },
    ],
  ],
};

export const categoriesConfig = {
  endpoint: '/api/data/categories-dashboard',
  testIdPrefix: 'categories-analytics',
  kpis: [
    { key: 'total', label: 'Total Categories', icon: Layers, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, subKey: 'inactive', subLabel: 'inactive', accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'total_budget', label: 'Total Budget', icon: DollarSign, accent: 'bg-amber-50 text-amber-600', format: fmtMoney },
    { key: 'inactive', label: 'Inactive', icon: Layers, accent: 'bg-zinc-100 text-zinc-600' },
  ],
  charts: [
    [
      { kind: 'pie', dataKey: 'by_parent', title: 'Categories by Parent', subtitle: 'Click a parent to drill', drillTo: { resource: '/api/data/categories', queryKey: 'parent_code', columns: [{key:'code',label:'Code'},{key:'description',label:'Description'},{key:'budget',label:'Budget',align:'right',format:(v)=>v?`$${Number(v).toLocaleString()}`:'—'}] } },
      { kind: 'bar-h', dataKey: 'items_per_cat', title: 'Top 8 Categories by Item Count', subtitle: 'Inventory diversity — click to drill', colorOffset: 2, drillTo: { resource: '/api/data/items', queryKey: 'category_code', columns: [{key:'item_code',label:'Code'},{key:'description',label:'Description'},{key:'unit_cost',label:'Unit Cost',align:'right',format:(v)=>v?`$${Number(v).toLocaleString()}`:'—'}] } },
    ],
    [
      { kind: 'table', dataKey: 'spend_by_cat', title: 'Top 10 Categories by Spend', subtitle: 'Click a category to see its orders', span: 'md:col-span-2', valueKey: 'value', extraKey: 'orders', extraLabel: 'POs', drillTo: { resource: '/api/orders', queryKey: 'category_code', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?`$${Number(v).toLocaleString()}`:'—'}] } },
      { kind: 'table', dataKey: 'top_budget', title: 'Top 10 Budgets', subtitle: 'Annual budget allocated', span: 'md:col-span-2', valueKey: 'value', extraKey: 'monthly', extraLabel: '/mo' },
    ],
  ],
};

export const currenciesConfig = {
  endpoint: '/api/data/currencies-dashboard',
  testIdPrefix: 'currencies-analytics',
  kpis: [
    { key: 'total', label: 'Total Currencies', icon: Globe, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'base_currency', label: 'Base Currency', icon: DollarSign, accent: 'bg-amber-50 text-amber-600' },
  ],
  charts: [
    [
      { kind: 'pie', dataKey: 'orders_by_cur', title: 'Order Volume by Currency', subtitle: 'Click a currency to drill into orders', drillTo: { resource: '/api/orders', queryKey: 'currency', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'currency',label:'Cur'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
      { kind: 'pie', dataKey: 'invoices_by_cur', title: 'Invoice Volume by Currency', subtitle: 'Click to see invoices in that currency', drillTo: { resource: '/api/invoices', queryKey: 'currency', columns: [{key:'invoice_number',label:'Invoice'},{key:'supplier_name',label:'Supplier'},{key:'invoice_gross_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
    ],
    [
      { kind: 'table', dataKey: 'rate_list', title: 'Exchange Rates', subtitle: 'Current rates vs base', span: 'md:col-span-4', valueKey: 'rate', moneyFormat: false },
    ],
  ],
};

export const deliveryPointsConfig = {
  endpoint: '/api/data/delivery-points-dashboard',
  testIdPrefix: 'delivery-points-analytics',
  kpis: [
    { key: 'total', label: 'Total Points', icon: MapPin, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, subKey: 'inactive', subLabel: 'inactive', accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'countries', label: 'Countries', icon: Globe, accent: 'bg-amber-50 text-amber-600' },
    { key: 'inactive', label: 'Inactive', icon: MapPin, accent: 'bg-zinc-100 text-zinc-600' },
  ],
  charts: [
    [
      { kind: 'pie', dataKey: 'by_country', title: 'Delivery Points by Country', subtitle: 'Geographic distribution' },
      { kind: 'bar-h', dataKey: 'by_state', title: 'Top 10 States/Regions', subtitle: 'Sub-national breakdown', colorOffset: 2 },
    ],
    [
      { kind: 'table', dataKey: 'top_dest', title: 'Top 10 Destinations by Volume', subtitle: 'Purchase orders shipped here', span: 'md:col-span-4', valueKey: 'spend', extraKey: 'value', extraLabel: 'POs' },
    ],
  ],
};

export const invoicePointsConfig = {
  endpoint: '/api/data/invoice-points-dashboard',
  testIdPrefix: 'invoice-points-analytics',
  kpis: [
    { key: 'total', label: 'Total Points', icon: Receipt, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'countries', label: 'Countries', icon: Globe, accent: 'bg-amber-50 text-amber-600' },
  ],
  charts: [
    [
      { kind: 'pie', dataKey: 'by_country', title: 'Points by Country', subtitle: 'Click to drill into country', drillTo: { resource: '/api/data/invoice-points', queryKey: 'country', columns: [{key:'code',label:'Code'},{key:'name',label:'Name'},{key:'city',label:'City'},{key:'country',label:'Country'}] } },
      { kind: 'bar', dataKey: 'inv_status', title: 'Invoice Status Mix', subtitle: 'Click a status to drill', colorOffset: 1, drillTo: { resource: '/api/invoices', queryKey: 'status', columns: [{key:'invoice_number',label:'Invoice'},{key:'supplier_name',label:'Supplier'},{key:'status',label:'Status'},{key:'invoice_gross_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
    ],
    [
      { kind: 'table', dataKey: 'top_points', title: 'Top 10 Points by Volume', subtitle: 'Click a destination to see orders', span: 'md:col-span-4', valueKey: 'spend', extraKey: 'value', extraLabel: 'POs', drillTo: { resource: '/api/orders', queryKey: 'invoice_point_name', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
    ],
  ],
};

export const documentTypesConfig = {
  endpoint: '/api/data/document-types-dashboard',
  testIdPrefix: 'document-types-analytics',
  kpis: [
    { key: 'total', label: 'Total Types', icon: FileSpreadsheet, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, subKey: 'inactive', subLabel: 'inactive', accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'inactive', label: 'Inactive', icon: FileText, accent: 'bg-zinc-100 text-zinc-600' },
  ],
  charts: [
    [
      { kind: 'bar', dataKey: 'usage_orders', title: 'Usage on Purchase Orders', subtitle: 'Click to see orders of that type', drillTo: { resource: '/api/orders', queryKey: 'document_type_code', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'document_type_code',label:'Doc Type'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
      { kind: 'bar', dataKey: 'usage_reqs', title: 'Usage on Requisitions', subtitle: 'Click to drill into requisitions', colorOffset: 2, drillTo: { resource: '/api/purchase-requisitions', queryKey: 'document_type', columns: [{key:'requisition_number',label:'Req'},{key:'description',label:'Description'},{key:'document_type',label:'Doc Type'}] } },
    ],
    [
      { kind: 'table', dataKey: 'budget_list', title: 'Top 10 by Budget', subtitle: 'Annual budget per document type', span: 'md:col-span-4', valueKey: 'value' },
    ],
  ],
};

export const termsConditionsConfig = {
  endpoint: '/api/data/terms-conditions-dashboard',
  testIdPrefix: 'tc-analytics',
  kpis: [
    { key: 'total', label: 'Total T&Cs', icon: Scroll, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'order_defaults', label: 'Order Defaults', icon: FileText, accent: 'bg-amber-50 text-amber-600' },
    { key: 'rfq_defaults', label: 'RFQ Defaults', icon: FileText, accent: 'bg-violet-50 text-violet-600' },
  ],
  charts: [
    [
      { kind: 'bar', dataKey: 'defaults_chart', title: 'Default-Flagged by Module', subtitle: 'How many T&Cs default to each workflow', colorOffset: 1 },
      { kind: 'pie', dataKey: 'file_types', title: 'T&C File Types', subtitle: 'Text vs. file attachments' },
    ],
    [
      { kind: 'bar-h', dataKey: 'linked_doc_count', title: 'Top 10 Most-Linked T&Cs', subtitle: 'Number of document types linked', span: 'md:col-span-4', colorOffset: 3 },
    ],
  ],
};

export const groupItemsConfig = {
  endpoint: '/api/data/group-items-dashboard',
  testIdPrefix: 'group-items-analytics',
  kpis: [
    { key: 'total', label: 'Total Groups', icon: GroupIcon, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'active', label: 'Active', icon: CheckCircle2, accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'total_items', label: 'Items in Groups', icon: Boxes, accent: 'bg-amber-50 text-amber-600' },
    { key: 'avg_items_per_group', label: 'Avg per Group', icon: BarChart2, accent: 'bg-violet-50 text-violet-600' },
  ],
  charts: [
    [
      { kind: 'bar-h', dataKey: 'items_per_group', title: 'Top 10 Groups by Item Count', subtitle: 'Group size distribution', span: 'md:col-span-4' },
    ],
  ],
};

export const accountCodesConfig = {
  endpoint: '/api/data/account-codes-dashboard',
  testIdPrefix: 'account-codes-analytics',
  kpis: [
    { key: 'segments', label: 'Segments', icon: Hash, accent: 'bg-indigo-50 text-indigo-600' },
    { key: 'values_active', label: 'Active Values', icon: CheckCircle2, accent: 'bg-emerald-50 text-emerald-600' },
    { key: 'values_total', label: 'Total Values', icon: Layers, accent: 'bg-amber-50 text-amber-600' },
    { key: 'total_budget', label: 'Total Budget', icon: DollarSign, accent: 'bg-violet-50 text-violet-600', format: fmtMoney },
  ],
  charts: [
    [
      { kind: 'bar', dataKey: 'by_segment', title: 'Values per Segment', subtitle: 'Distribution across segment numbers' },
      { kind: 'table', dataKey: 'top_spend', title: 'Top 10 Codes by Spend', subtitle: 'Click a code to drill into orders', valueKey: 'value', drillTo: { resource: '/api/orders', queryKey: 'account_code', columns: [{key:'order_number',label:'PO'},{key:'supplier_name',label:'Supplier'},{key:'order_total',label:'Total',align:'right',format:(v)=>v?Number(v).toLocaleString(undefined,{minimumFractionDigits:2}):'—'}] } },
    ],
    [
      { kind: 'table', dataKey: 'top_budget', title: 'Top 10 Codes by Budget', subtitle: 'Annual budget allocated', span: 'md:col-span-4', valueKey: 'value', extraKey: 'monthly', extraLabel: '/mo' },
    ],
  ],
};
